import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
} from 'firebase/auth';
import { environment } from './../../../environments/environment';
import { DbUser } from '@core/models/user.model';
import { resolveActiveClientId } from '@core/utils/active-client-id.util';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private auth = getAuth();
  private http = inject(HttpClient);

  private firebaseUser = signal<User | null>(null);
  private dbUser = signal<DbUser | null>(null);
  private loading = signal(true);
  /** Evita que respuestas viejas de `/users/me` pisen un perfil ya cargado (p. ej. registro). */
  private profileLoadGeneration = 0;
  /**
   * Durante el alta, el listener no debe llamar a `/users/me` hasta que exista el cliente en API.
   * Se activa con `beginDeferProfileFetch()` antes de `createUserWithEmailAndPassword`.
   */
  private deferProfileFetchFromListener = false;

  isAuthenticated = computed(() => this.firebaseUser() !== null);
  isLoading = computed(() => this.loading());
  isAdmin = computed(() => this.dbUser()?.isAdmin ?? false);
  user = computed(() => this.dbUser());
  /** Cliente activo para rutas de cliente (p. ej. proyectos). */
  activeClientId = computed(() => resolveActiveClientId(this.dbUser()));

  constructor() {
    onAuthStateChanged(this.auth, async (user) => {
      this.loading.set(true);
      this.firebaseUser.set(user);

      if (user) {
        if (this.deferProfileFetchFromListener) {
          this.dbUser.set(null);
        } else {
          const gen = ++this.profileLoadGeneration;
          const data = await this.fetchDbUser();
          if (gen === this.profileLoadGeneration) {
            this.dbUser.set(data);
          }
        }
      } else {
        this.profileLoadGeneration++;
        this.dbUser.set(null);
      }

      this.loading.set(false);
    });
  }

  private async fetchDbUser(): Promise<DbUser | null> {
    try {
      const userData = await firstValueFrom(
        this.http.get<DbUser>(`${environment.apiUrl}/users/me`),
      );
      return userData ?? null;
    } catch (error) {
      console.error('Error obteniendo el contexto del usuario:', error);
      return null;
    }
  }

  /** Llamar antes de `createUserWithEmailAndPassword` en el flujo de registro. */
  beginDeferProfileFetch(): void {
    this.deferProfileFetchFromListener = true;
  }

  /** Llamar en `finally` del registro. */
  endDeferProfileFetch(): void {
    this.deferProfileFetchFromListener = false;
  }

  /**
   * Vuelve a cargar el usuario de backoffice desde la API.
   * Tras `createClient` en el registro, aquí va el primer `/users/me` válido.
   */
  async refreshDbUser(): Promise<void> {
    if (!this.auth.currentUser) {
      return;
    }
    this.loading.set(true);
    const gen = ++this.profileLoadGeneration;
    try {
      let profile: DbUser | null = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        profile = await this.fetchDbUser();
        if (profile !== null) {
          break;
        }
        if (attempt < 2) {
          await new Promise((r) => setTimeout(r, 400));
        }
      }
      if (gen === this.profileLoadGeneration) {
        this.dbUser.set(profile);
      }
    } finally {
      this.loading.set(false);
    }
  }

  async login(email: string, password: string): Promise<void> {
    this.loading.set(true);
    try {
      await signInWithEmailAndPassword(this.auth, email, password);
    } catch (error) {
      this.loading.set(false);
      throw error;
    }
    await this.waitUntilLoadingFalse();
  }

  private waitUntilLoadingFalse(): Promise<void> {
    const deadline = Date.now() + 30_000;
    return new Promise((resolve, reject) => {
      const tick = (): void => {
        if (!this.loading()) {
          resolve();
          return;
        }
        if (Date.now() > deadline) {
          reject(new Error('Auth profile load timed out'));
          return;
        }
        setTimeout(tick, 10);
      };
      tick();
    });
  }

  async logout(): Promise<void> {
    this.profileLoadGeneration++;
    await signOut(this.auth);
    this.dbUser.set(null);
  }

  async getIdToken(): Promise<string | null> {
    const user = this.auth.currentUser;
    if (!user) return null;
    return user.getIdToken();
  }
}
